import { createSelector } from '@reduxjs/toolkit';

import { selectDetailedPricing } from './detailedPricingSlice';

import type { DetailedPricing, LineItem } from './api';

export interface DiscountInfo {
    /** Discount in major units (e.g., dollars for USD). Always positive — only populated when discountTotal > 0. */
    amount: number;
    currency: string;
}

const EMPTY_DISCOUNTS: Readonly<Partial<Record<string, DiscountInfo | null>>> = Object.freeze({});

// Returns a record keyed by SKU. null = line item exists but has no positive
// discount (zero or negative discountTotal — negative should not occur in
// practice; api.ts warns if it does); absent key = line item not in pricing
// data; DiscountInfo = active discount with amount and currency.
export const selectDiscountsByTrip = createSelector(
    selectDetailedPricing,
    (pricing: DetailedPricing | undefined): Partial<Record<string, DiscountInfo | null>> => {
        if (!pricing) return EMPTY_DISCOUNTS;
        const result: Partial<Record<string, DiscountInfo | null>> = {};
        for (const lineItem of pricing.lineItems.filter((li) => li.lineItemType === 'trip')) {
            result[lineItem.sku] = lineItem.displayTotals.discountTotal > 0
                ? {
                    amount: lineItem.displayTotals.discountTotal,
                    currency: lineItem.displayTotals.currency,
                }
                : null;
        }
        return result;
    },
);

/**
 * Returns the travel insurance line item (lineItemType === 'travel_insurance') if present.
 */
export const selectInsuranceLineItem = createSelector(
    selectDetailedPricing,
    (pricing: DetailedPricing | undefined): LineItem | undefined => {
        if (!pricing) return undefined;
        return pricing.lineItems.find((li) => li.lineItemType === 'travel_insurance');
    },
);

/**
 * Returns trip line items (lineItemType === 'trip') from detailedPricing,
 * in the order returned by the API (expected: departure first, then return).
 * Other line item types (e.g. 'travel_insurance', 'refundable_addon') are
 * excluded — use selectInsuranceLineItem for insurance. The 'refundable_addon'
 * type is declared in LineItemType but not yet surfaced in the UI.
 * In practice, all orders with detailedPricing contain at least one trip-type
 * line item (insurance cannot be purchased without a trip).
 *
 * Returns `undefined` in two cases: pricing not yet loaded, or pricing loaded
 * with no trip items. Both are intentionally conflated — callers should fall
 * back to quoteSummary-derived data in either case.
 */
export const selectTripLineItems = createSelector(
    selectDetailedPricing,
    (pricing: DetailedPricing | undefined): LineItem[] | undefined => {
        if (!pricing) return undefined;
        const trips = pricing.lineItems.filter((li) => li.lineItemType === 'trip');
        return trips.length > 0
            ? trips
            : undefined;
    },
);
